/**
 * What the working-set screen shows about the rep target and the result of a logged set (#4142).
 *
 * Pure and Angular-free, so every row of both display tables is unit-tested away from the page.
 * The rules, the reasons behind them and the mock-ups: `design-docs/working-set-screen.md`.
 *
 * These functions only DISPLAY numbers the progression logic already keeps. They decide nothing
 * about progression, and they never write to a plan path.
 */
import { WEIGHT_GRID_TOLERANCE_KG } from '@cf-shared/data-model/equipment';
import { SetSuccess, SetType } from '@cf-shared/enums';
import { LogRecordState } from '@cf-shared/data-model/user-log';
import { DateTime } from 'luxon';
import { RepProgressBarTone } from 'components/cs-rep-progress-bar/rep-progress-bar-tone';

/**
 * The plan-path fields the screen reads. Typed loosely on purpose: a path written by an older
 * version of the app, or a damaged one, may hold `null`, a string or `NaN` in any of them, and
 * `min_reps` is simply absent on a path that has never concluded a set.
 */
export interface RepRangePathFields {
  min_reps?: unknown;
  target_reps?: unknown;
  lastStats?: unknown;
}

/** A weight and a rep count, both finite, ready to print as "<weight> × <reps>". */
export interface WeightAndReps {
  weight_kg: number;
  reps: number;
  /** the unit the weight was logged in, passed on to the page's weight formatter */
  weight_unit?: string;
}

export interface RepRangeDisplayInput {
  setType: SetType;
  /**
   * The path the open set was prescribed from. The caller passes the set's `planPathBackup` when
   * it carries one (a reopened, already concluded set) and the live plan path otherwise.
   */
  path: RepRangePathFields | null | undefined;
  /**
   * `ConfigKey.CompactWorkoutFeedback` is on. Off (or absent), the answer is exactly what it was
   * before the review fixes, so the switch-off screens do not change
   */
  compactFeedback?: boolean;
  /** the weight the open set is given (`plannedWeight_kg`); read only with `compactFeedback` on */
  givenWeight_kg?: unknown;
}

export interface RepRangeDisplay {
  /**
   * the bar from the floor to the target; absent = no bar. `lastReps` places the tick at last time's
   * reps; with the compact switch on it is left out when last time was at another weight
   */
  range?: { floor: number; target: number; lastReps?: number };
  /** the "Last session <weight> × <reps>" line; absent = no line */
  lastSession?: WeightAndReps;
  /**
   * the Reps field's placeholder: "8–13" when `floor` is present, "13" when it is not.
   * Absent = the field keeps its own placeholder, as before #4142.
   */
  placeholder?: { floor?: number; target: number };
  /**
   * where the reps picker lands. Absent = keep today's landing value, which the page works out
   * with `getSuggestedPreselectedRep` (it needs the set list, which this pure helper cannot see).
   */
  suggestedReps?: number;
}

/** The headline of a completed benchmark set's confirmation. */
export enum SetProgressionHeadline {
  /** "Rep target hit!" */
  TargetHit = 'TargetHit',
  /** "You progressed!" */
  Progressed = 'Progressed',
  /** "Set logged" — neutral, no praise and no blame */
  Logged = 'Logged',
}

export interface SetProgressionInput {
  setType: SetType;
  /** what `concludeSet` decided for the set */
  setSuccess: unknown;
  /** the logged stats */
  stats: unknown;
  /** the weight the set was given when it opened (the rounded prescription) */
  plannedWeight_kg: unknown;
  /** the clone of the plan path `concludeSet` took BEFORE any progression write */
  planPathBackup: RepRangePathFields | null | undefined;
  /** `true` when the user declined the deload this set was offered (#6309); absent on older sets */
  deloadDeclined?: unknown;
  /** `true` when a progress set was no stronger than the previous session's best set (#6447); absent on older sets */
  noGainOverLastSession?: unknown;
}

export interface SetProgressionResult {
  headline: SetProgressionHeadline;
  /** what the set did */
  done: WeightAndReps;
  /** "+N over last time", only when N > 0 on a progress headline */
  overLastTime?: number;
  /** "Next session: aim for N+", on the "You progressed!" headline only */
  aimFor?: number;
  /** "Last time you did N.", on the neutral headline only */
  lastTimeReps?: number;
  /**
   * last time's weight and reps, from the backup's `lastStats`, on every headline; absent when that
   * is not usable. This is the set the engine recorded as the one to beat, usually but not always
   * last session's set 1 (`design-docs/working-set-screen.md`)
   */
  lastTime?: WeightAndReps;
  /** the "Last time" and "Today" comparison bars, both on a 0 → `scaleMax` scale; absent = no bars */
  bars?: { scaleMax: number; lastReps: number; todayReps: number; tone: RepProgressBarTone };
}

/** A value is usable only when it is a finite number. Anything else means "not there". */
function finiteOrUndefined(value: unknown): number | undefined {
  return typeof value === 'number' && Number.isFinite(value) ? value : undefined;
}

/** Reads a stored set's weight and reps, or `undefined` when either is not a finite number. */
export function readWeightAndReps(stats: unknown): WeightAndReps | undefined {
  if (!stats || typeof stats !== 'object') {
    return undefined;
  }
  const record = stats as Record<string, unknown>;
  const weight_kg = finiteOrUndefined(record.weight_kg);
  const reps = finiteOrUndefined(record.reps);
  if (weight_kg === undefined || reps === undefined) {
    return undefined;
  }
  const result: WeightAndReps = { weight_kg, reps };
  if (typeof record.weight_unit === 'string') {
    result.weight_unit = record.weight_unit;
  }
  return result;
}

/** Decides what an OPEN set shows before it is logged. See the display-rule table in the design doc. */
export function getSetRepRangeDisplay(input: RepRangeDisplayInput): RepRangeDisplay {
  // every set type except the benchmark set is left exactly as it was. That includes a follow-up:
  // set 1's confirmation, right above it, already shows what set 1 did (owner decision 2026-09-28)
  if (input.setType !== SetType.Regular1st) {
    return {};
  }

  const target = finiteOrUndefined(input.path?.target_reps);
  const floor = finiteOrUndefined(input.path?.min_reps);
  const lastSession = readWeightAndReps(input.path?.lastStats);

  // without a target there is nothing to show softly and nowhere new to land the picker
  if (target === undefined) {
    return lastSession ? { lastSession } : {};
  }
  // without last time there is no tick, so no range, and no last-session line
  if (!lastSession) {
    return { placeholder: { target }, suggestedReps: target };
  }
  if (input.compactFeedback) {
    return getCompactRepRangeDisplay(input, target, lastSession);
  }
  // a floor of 1 means the weight just went up; a floor above the target was left there when the
  // user lowered their target, and the target is what decides such a set, so neither is a range
  if (floor !== undefined && floor > 1 && floor < target) {
    return {
      range: { floor, target, lastReps: lastSession.reps },
      lastSession,
      placeholder: { floor, target },
      suggestedReps: floor,
    };
  }
  return { lastSession, placeholder: { target }, suggestedReps: target };
}

// the open benchmark set with the compact switch on (#4142 review fixes, R5.2): the range runs from
// the display floor, which after a planned weight increase is 1, to the target
function getCompactRepRangeDisplay(input: RepRangeDisplayInput, target: number, lastSession: WeightAndReps): RepRangeDisplay {
  const floor = getDisplayFloor(input.path, input.givenWeight_kg);
  // a single value (floor at or above the target) is not a range
  if (floor === undefined || floor < 1 || floor >= target) {
    return { lastSession, placeholder: { target }, suggestedReps: target };
  }
  const range: RepRangeDisplay['range'] = { floor, target };
  // the tick says "at least N, because you got N - 1"; at another weight that comparison means nothing
  if (isSameWeight(lastSession.weight_kg, finiteOrUndefined(input.givenWeight_kg))) {
    range.lastReps = lastSession.reps;
  }
  // landing on 1 helps nobody, so a floor of 1 lands on the target
  return { range, lastSession, placeholder: { floor, target }, suggestedReps: floor > 1 ? floor : target };
}

/**
 * The lowest rep count the screen shows as progress for a benchmark set (#4142 review fixes, R5.1).
 * One rule for the mark, the open set's range, the Target line and the bar. Display only: it is
 * never written anywhere. First match wins:
 *
 * 1. no usable last time, or no usable given weight: none;
 * 2. given heavier than last time (a planned weight increase): 1;
 * 3. given the same as last time: the stored `min_reps` when above 1, else last reps + 1 (Menno's
 *    rule, for when the app raised only the rep target, which resets `min_reps` to 1);
 * 4. given lighter than last time (a deload, or the #5176 cap): the stored `min_reps`, else none.
 */
export function getDisplayFloor(path: RepRangePathFields | null | undefined, givenWeight_kg: unknown): number | undefined {
  const last = readWeightAndReps(path?.lastStats);
  const given = finiteOrUndefined(givenWeight_kg);
  if (!last || given === undefined) {
    return undefined;
  }
  const storedFloor = finiteOrUndefined(path?.min_reps);
  if (given - last.weight_kg > WEIGHT_GRID_TOLERANCE_KG) {
    return 1;
  }
  if (isSameWeight(given, last.weight_kg)) {
    return storedFloor !== undefined && storedFloor > 1 ? storedFloor : last.reps + 1;
  }
  return storedFloor;
}

/** The mark a logged benchmark set shows with the compact switch on: a coloured badge and a word chip. */
export enum SetOutcomeMark {
  /** green "Target hit": in or above the target range */
  Success = 'success',
  /** orange "Same as last time": the same reps at the same weight */
  Same = 'same',
  /** red "Fewer reps": fewer reps at the same weight */
  Fewer = 'fewer',
  /** grey badge, no chip */
  None = 'none',
}

/**
 * Set 1's mark (#4142 review fixes, R5.1), from the set's own stored data only, so it never changes
 * once the set is logged. Display only: what the app decided and stored is unchanged. First match wins:
 *
 * | Case | Mark |
 * |---|---|
 * | no backup, or no usable logged set | none |
 * | done ≈ given, given heavier than last time | green |
 * | done ≈ given ≈ last, reps ≥ min(floor, target) (the floor alone without a target) | green |
 * | done ≈ given ≈ last, reps = last reps | orange |
 * | done ≈ given ≈ last, reps < last reps | red |
 * | done ≈ given ≈ last, between last reps and the floor | none |
 * | any other case | green if the app scored progress, else none |
 */
export function getSetOutcomeMark(input: SetProgressionInput): SetOutcomeMark {
  const result = getSetProgressionResult(input);
  const backup = input.planPathBackup;
  if (!result || !backup) {
    return SetOutcomeMark.None;
  }
  const done = result.done;
  const last = readWeightAndReps(backup.lastStats);
  const given = finiteOrUndefined(input.plannedWeight_kg);
  // what the app scored: heavier or lighter than given, given lighter than last time, or no usable last time
  const scoredMark = result.headline === SetProgressionHeadline.Logged ? SetOutcomeMark.None : SetOutcomeMark.Success;
  if (!last || given === undefined || !isSameWeight(done.weight_kg, given)) {
    return scoredMark;
  }
  if (given - last.weight_kg > WEIGHT_GRID_TOLERANCE_KG) {
    return SetOutcomeMark.Success;
  }
  if (!isSameWeight(given, last.weight_kg)) {
    return scoredMark;
  }
  // the floor is always a number here: last time and the given weight are both usable
  const floor = getDisplayFloor(backup, given);
  const target = finiteOrUndefined(backup.target_reps);
  const enough = target === undefined ? floor : Math.min(floor, target);
  if (done.reps >= enough) {
    return SetOutcomeMark.Success;
  }
  if (done.reps === last.reps) {
    return SetOutcomeMark.Same;
  }
  if (done.reps < last.reps) {
    return SetOutcomeMark.Fewer;
  }
  return SetOutcomeMark.None;
}

// two weights are the same when they agree within the equipment grid's tolerance (lb values are
// stored as converted kg); an unusable weight is never the same as anything
function isSameWeight(a: number | undefined, b: number | undefined): boolean {
  return a !== undefined && b !== undefined && Math.abs(a - b) <= WEIGHT_GRID_TOLERANCE_KG;
}

/**
 * Decides the confirmation a COMPLETED benchmark set shows in its collapsed row, from the set's
 * own stored data only. Returns `undefined` when there is nothing to confirm: not a benchmark set,
 * or no usable logged stats.
 */
export function getSetProgressionResult(input: SetProgressionInput): SetProgressionResult | undefined {
  if (input.setType !== SetType.Regular1st) {
    return undefined;
  }
  const done = readWeightAndReps(input.stats);
  if (!done) {
    return undefined;
  }
  // a set concluded before the backup existed cannot say what "last time" was, so it stays neutral
  const backup = input.planPathBackup;
  if (!backup) {
    return { headline: SetProgressionHeadline.Logged, done };
  }

  const lastTime = readWeightAndReps(backup.lastStats);
  const lastReps = lastTime?.reps;
  const scaleMax = finiteOrUndefined(backup.target_reps);
  // a declined deload is never praised; nor is progress that was no stronger than last session (#6447)
  const isNoGainProgress = input.setSuccess === SetSuccess.MinReps && input.noGainOverLastSession === true;
  const headline = input.deloadDeclined === true || isNoGainProgress
    ? SetProgressionHeadline.Logged
    : headlineFor(input.setSuccess, done, finiteOrUndefined(backup.min_reps), finiteOrUndefined(input.plannedWeight_kg));

  const result: SetProgressionResult = { headline, done };
  if (lastTime) {
    result.lastTime = lastTime;
  }
  if (headline === SetProgressionHeadline.Logged) {
    if (lastReps !== undefined) {
      result.lastTimeReps = lastReps;
    }
  } else if (lastReps !== undefined && done.reps > lastReps) {
    result.overLastTime = done.reps - lastReps;
  }
  // this is what concludeSet wrote into min_reps when it scored the set as progress
  if (headline === SetProgressionHeadline.Progressed) {
    result.aimFor = done.reps + 1;
  }
  if (lastReps !== undefined && scaleMax !== undefined && scaleMax > 0) {
    result.bars = {
      scaleMax,
      lastReps,
      todayReps: done.reps,
      tone: headline === SetProgressionHeadline.Logged ? RepProgressBarTone.Neutral : RepProgressBarTone.Progress,
    };
  }
  return result;
}

/** Picks the confirmation's headline from what `concludeSet` stored for the set. */
function headlineFor(setSuccess: unknown, done: WeightAndReps, backupFloor: number | undefined, plannedWeight_kg: number | undefined): SetProgressionHeadline {
  if (setSuccess === SetSuccess.TargetReps) {
    return SetProgressionHeadline.TargetHit;
  }
  if (setSuccess !== SetSuccess.MinReps) {
    return SetProgressionHeadline.Logged;
  }
  // a declined deload is also stored as MinReps, without raising the floor. Since #6309 it carries
  // `deloadDeclined`, read before this; for a set logged before that, a set below the floor it was
  // shown, and not heavier than the weight it was given, is read as one (owner decision 2026-09-28)
  const heavierThanGiven = plannedWeight_kg !== undefined && done.weight_kg - plannedWeight_kg > WEIGHT_GRID_TOLERANCE_KG;
  if (backupFloor !== undefined && done.reps < backupFloor && !heavierThanGiven) {
    return SetProgressionHeadline.Logged;
  }
  return SetProgressionHeadline.Progressed;
}

/** What set 1's FOLDED row shows while the compact-feedback switch is on: one line. */
export interface CompactSetRowDisplay {
  mark: SetOutcomeMark;
  /** today's weight × reps */
  done: WeightAndReps;
}

/**
 * Builds set 1's folded row (#4142 review fixes, R1): the mark and today's weight × reps. Derived only
 * from the set's own stored data, so it never changes once the set is logged. Undefined when the set
 * has no progression result. See `design-docs/working-set-screen.md`, "Compact feedback switch".
 */
export function getCompactSetRowDisplay(input: SetProgressionInput): CompactSetRowDisplay | undefined {
  const result = getSetProgressionResult(input);
  return result ? { mark: getSetOutcomeMark(input), done: result.done } : undefined;
}

/** a set's stored log time in the zone asked for (local by default); undefined when it does not read as a real moment */
export function getSetLoggedTime(completedTime_utc: unknown, zone?: string): DateTime | undefined {
  if (typeof completedTime_utc !== 'string') {
    return undefined;
  }
  const completed = DateTime.fromISO(completedTime_utc, { zone: 'utc' });
  if (!completed.isValid) {
    return undefined;
  }
  return zone ? completed.setZone(zone) : completed.toLocal();
}

/** What set 1 shows UNFOLDED while the compact-feedback switch is on: a list under its row. */
export interface SetDetailDisplay {
  mark: SetOutcomeMark;
  /** "Target <floor>–<target> reps", or "<target> reps" without a usable range; absent without a usable target */
  target?: { floor?: number; target: number };
  /**
   * the bar on the 0 → target scale: the green part from `floor` to the target (none without a
   * floor), the tick at `lastReps` (only when last time was at the given weight) and a dot at
   * today's reps. Absent without a usable target
   */
  bar?: { scaleMax: number; floor?: number; lastReps?: number; todayReps: number };
  /** "Last time <weight × reps>"; absent when last time is not usable */
  lastTime?: WeightAndReps;
  /** "Logged at <time>", in the zone asked for; absent when the stored time is missing or unreadable */
  loggedAt?: DateTime;
  /** "+N reps (vs. last time)": only when the reps went up at last time's weight */
  overLastTime?: number;
  /** "Next session: aim for N+": N is exactly what `concludeSet` wrote into `min_reps` */
  aimFor?: number;
}

/**
 * Builds set 1's unfolded details (#4142 review fixes, R2.3 and R5.3) from the set's own stored data
 * only. `zone` is the zone the log time is shown in (the device's own by default). Undefined when the
 * set has no progression result.
 */
export function getSetDetailDisplay(input: SetProgressionInput, completedTime_utc: unknown, zone?: string): SetDetailDisplay | undefined {
  const result = getSetProgressionResult(input);
  if (!result) {
    return undefined;
  }
  const mark = getSetOutcomeMark(input);
  const detail: SetDetailDisplay = { mark };
  const backup = input.planPathBackup;
  const target = finiteOrUndefined(backup?.target_reps);
  const given = finiteOrUndefined(input.plannedWeight_kg);
  const done = result.done;
  // if the set has a usable target, it gets the Target line and the bar
  if (backup && target !== undefined && target > 0) {
    const range = getTargetRange(backup, given);
    detail.target = range;
    detail.bar = { scaleMax: target, todayReps: done.reps };
    if (range.floor !== undefined) {
      detail.bar.floor = range.floor;
    }
    if (result.lastTime && isSameWeight(result.lastTime.weight_kg, given)) {
      detail.bar.lastReps = result.lastTime.reps;
    }
  }
  if (result.lastTime) {
    detail.lastTime = result.lastTime;
  }
  const loggedAt = getSetLoggedTime(completedTime_utc, zone);
  if (loggedAt) {
    detail.loggedAt = loggedAt;
  }
  const gain = getRepGainAtSameWeight(done, result.lastTime);
  if (gain !== undefined) {
    detail.overLastTime = gain;
  }
  // this is what concludeSet wrote into min_reps when it scored the set as progress
  const scoredMinReps = input.setSuccess === SetSuccess.MinReps && input.deloadDeclined !== true;
  if (mark === SetOutcomeMark.Success && scoredMinReps && target !== undefined && done.reps < target) {
    detail.aimFor = done.reps + 1;
  }
  return detail;
}

/**
 * The Target text of set 1's details (#4142 review fixes, R5.3): "<floor>–<target> reps" when the
 * display floor is a range below the target, otherwise "<target> reps". The caller passes a path
 * with a usable target.
 */
function getTargetRange(path: RepRangePathFields, givenWeight_kg: number | undefined): { floor?: number; target: number } {
  const target = finiteOrUndefined(path.target_reps) as number;
  const floor = getDisplayFloor(path, givenWeight_kg);
  return floor !== undefined && floor >= 1 && floor < target ? { floor, target } : { target };
}

/**
 * "+N reps (vs. last time)": how many more reps a set did than the one it is compared with, at the
 * same weight. Undefined when either is missing, the weights differ, or the reps did not go up.
 * Used for set 1 against `lastStats` and for a later set against last session's same set.
 */
export function getRepGainAtSameWeight(done: WeightAndReps | undefined, last: WeightAndReps | undefined): number | undefined {
  if (!done || !last || !isSameWeight(done.weight_kg, last.weight_kg) || done.reps <= last.reps) {
    return undefined;
  }
  return done.reps - last.reps;
}

/** The fields of a logged set that decide whether it is the most recent one. Loosely typed for old or damaged documents. */
export interface OfferedSetFields {
  state?: unknown;
  completedTime_utc?: unknown;
}

export interface RestTimerStartOfferInput {
  /** `RestTimerService.isAllowedOnThisPlatform()`: the native apps, or the test-mode web toggle */
  platformAllowed: boolean;
  /** the workout is today's (`WorkoutLoadMode.Current`), not a past one */
  isCurrentWorkout: boolean;
  isCoachManaging: boolean;
  /** the benchmark set whose confirmation would carry the button */
  set: OfferedSetFields;
  /** every set in the workout, across all exercises, `set` included */
  allSets: OfferedSetFields[];
}

/**
 * Whether the completed benchmark set's confirmation offers "Start Rest Timer" (#6014).
 *
 * The confirmation is permanent, so the button is limited to where starting a timer makes sense:
 * a platform where the timer runs, today's workout, no coach at the controls, and only while this
 * set is the most recently logged one in the whole workout. A set logged at the same moment does
 * not hide it. It does not depend on the auto-start setting or on a timer already running.
 * The reasons: `design-docs/working-set-screen.md`.
 */
export function shouldOfferRestTimerStart(input: RestTimerStartOfferInput): boolean {
  if (!input.platformAllowed || !input.isCurrentWorkout || input.isCoachManaging) {
    return false;
  }
  const ownMillis = getDoneSetCompletedMillis(input.set);
  // if the set is not done, or its time is unreadable, it cannot be shown to be the latest
  if (ownMillis === undefined) {
    return false;
  }
  // if any other done set in the workout was logged later, this one is no longer the latest
  for (const other of input.allSets) {
    const otherMillis = getDoneSetCompletedMillis(other);
    if (otherMillis !== undefined && otherMillis > ownMillis) {
      return false;
    }
  }
  return true;
}

export interface RestTimerButtonInput {
  /** the exercise's rest time, shown while no timer counts down */
  restSeconds: number;
  /** `RestTimerService` signals; loosely typed so a missing or broken value cannot lock the button */
  remainingSeconds: unknown;
  totalSeconds: unknown;
  isRunning: boolean;
  isExpired: boolean;
}

export interface RestTimerButtonState {
  /** true while a rest counts down: the button shows the time left and cannot be tapped */
  counting: boolean;
  /** the seconds the label shows: the time left while counting, else the exercise's rest time */
  shownSeconds: number;
  /**
   * the share of the rest still to go, 0 to 1. 1 while not counting: the idle button draws no fill,
   * and the fill slides between values, so holding it full makes a starting countdown begin full
   * instead of sliding in from empty (#6640)
   */
  fillFraction: number;
}

/**
 * What the compact Rest Timer button shows (#6575). While any rest timer counts down, however it
 * started, the button shows the time left, a fill for the share still to go, and cannot be tapped.
 * Once the timer ends ("Done!"), is skipped or is cancelled, it offers the full rest time again.
 * A missing or unreadable remaining time reads as "not counting", so the button never stays locked.
 */
export function getRestTimerButtonState(input: RestTimerButtonInput): RestTimerButtonState {
  const idle: RestTimerButtonState = { counting: false, shownSeconds: input.restSeconds, fillFraction: 1 };
  const remaining = input.remainingSeconds;
  // if no timer counts down, or its time cannot be read, offer the full rest time
  if (!input.isRunning || input.isExpired || typeof remaining !== 'number' || !Number.isFinite(remaining) || remaining <= 0) {
    return idle;
  }
  const total = input.totalSeconds;
  // if the total is missing or broken, show a full fill rather than none
  const fillFraction = typeof total === 'number' && Number.isFinite(total) && total > 0
    ? Math.min(1, remaining / total)
    : 1;
  return { counting: true, shownSeconds: Math.round(remaining), fillFraction };
}

// the moment a done set was logged, in epoch milliseconds; undefined for a set that is not done
// or whose stored time is missing or unreadable
function getDoneSetCompletedMillis(set: OfferedSetFields): number | undefined {
  if (set.state !== LogRecordState.Done || typeof set.completedTime_utc !== 'string') {
    return undefined;
  }
  const completed = DateTime.fromISO(set.completedTime_utc);
  return completed.isValid ? completed.toMillis() : undefined;
}

/** The status-message lists stored on a set. Loosely typed: an old or damaged document may hold anything here. */
export interface SetStatusLists {
  preStatus?: unknown;
  postStatus?: unknown;
}

/**
 * How many of a set's status messages the user dismissed, for the status bell's badge while the
 * compact-feedback switch is on (#4142 follow-up): the bell then counts only what is hidden, and
 * tapping it shows those again. A list that is not an array counts as empty.
 */
export function getHiddenStatusMessageCount(set: SetStatusLists | null | undefined): number {
  return countDismissed(set?.preStatus) + countDismissed(set?.postStatus);
}

// the number of entries in a stored status list that are marked dismissed
function countDismissed(list: unknown): number {
  if (!Array.isArray(list)) {
    return 0;
  }
  let count = 0;
  for (const status of list) {
    if (status && typeof status === 'object' && (status as { dismissed?: unknown }).dismissed === true) {
      count++;
    }
  }
  return count;
}
